import { useMutation, useQueryClient } from "react-query";
import { AxiosError } from "axios";
import { putEvent, PutEvent, UserEventResponse } from "@/api";
import { useUser } from "@/providers/context";
import { EVENT_TYPE_JOURNAL } from "@/const";

export const usePutEvent = () => {
  const { uid } = useUser();
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError<{ detail: string }>, PutEvent>(
    (variables) => {
      if (!uid) {
        return Promise.reject();
      }
      return putEvent({ ...variables, UserId: uid });
    },
    {
      onSuccess: async (data, { ItemId, EventDate, EventType, ItemCourseId }) => {
        if(ItemCourseId) {
          queryClient.invalidateQueries(["course-completion", uid, ItemCourseId]);
          queryClient.invalidateQueries(["course-completion-all", uid]);
        }

        const responseData = data as UserEventResponse;
        const newEvent = responseData.Events.find(
          (event) =>
            event.ItemId === ItemId.toString() && event.EventDate === EventDate,
        );

        const existingData = queryClient.getQueryData(["userEvent", uid]) as
          | UserEventResponse
          | undefined;

        if (newEvent && existingData) {
          let existingIdx = -1;
          if (EventType === EVENT_TYPE_JOURNAL) {
            // If a journal entry has been updated, find the existing event to update
            existingIdx = existingData.Events.findIndex(
              (event) =>
                event.ItemId === ItemId.toString() &&
                event.EventDate === EventDate,
            );
          }

          const updatedEvents = [...existingData.Events];
          if (existingIdx !== -1) {
            updatedEvents[existingIdx] = newEvent;
          } else {
            updatedEvents.push(newEvent);
          }

          queryClient.setQueryData(["userEvent", uid], {
            ...existingData,
            Events: updatedEvents,
          } as UserEventResponse);
        }
      },
    },
  );
};
